import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneRight, Sparkle, X, CaretDown, ArrowLeft, CircleNotch } from "@phosphor-icons/react";
import { agentsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const AGENT_ICONS = {
    MGR: "👑", ACCT: "🔑", TRAVEL: "✈", DATE: "❤",
    ATLAS: "🗺", FEED: "🌐", MOD: "🛡", BILL: "💳",
    TECH: "🔧", SEC: "🔐", KB: "📚", INS: "📊",
};

export default function SupportChat({ variant = "page", onClose }) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [ticketId, setTicketId] = useState(null);
    const scrollRef = useRef(null);

    const STARTERS = t("supportChat.starters", { returnObjects: true }) || [
        "My Atlas isn't showing my stamps",
        "How do I upgrade to Premium?",
        "Reset my password",
        "Plan a $2,000 weekend in Miami",
        "I'm being charged twice",
    ];

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, busy]);

    const send = async (raw) => {
        const msg = (raw ?? text).trim();
        if (!msg || busy) return;
        setText("");
        setBusy(true);
        setMessages((m) => [...m, { role: "user", text: msg }]);
        try {
            const res = ticketId
                ? await agentsApi.replyTicket(ticketId, msg)
                : await agentsApi.createTicket(msg);
            if (res.ticket_id) setTicketId(res.ticket_id);
            setMessages((m) => [
                ...m,
                {
                    role: "manager",
                    text: res.reply,
                    delegations: res.delegations || [],
                    next_steps: res.next_steps || [],
                    intent: res.intent,
                    resolved: res.resolved,
                    escalate: res.escalate_to_owner,
                },
            ]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("supportChat.toasts.managerError"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={variant === "bubble" ? "flex flex-col h-full" : "flex flex-col min-h-[70vh]"} data-testid="support-chat">
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto space-y-4 p-4 md:p-5"
                style={{ background: variant === "bubble" ? "transparent" : "var(--navy-deep)", borderRadius: variant === "bubble" ? 0 : 24, border: variant === "bubble" ? "0" : "1px solid rgba(255,255,255,0.06)", maxHeight: variant === "bubble" ? "58vh" : "60vh" }}
            >
                {messages.length === 0 && (
                    <div className="text-white/60 text-sm space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="text-2xl">👑</span>
                            <div>
                                <div className="text-white font-medium">{t("supportChat.managerName")}</div>
                                <div className="text-white/50 text-[11px] tracking-[0.15em] uppercase">{t("supportChat.managerRole")}</div>
                            </div>
                        </div>
                        <p>{t("supportChat.managerIntro")}</p>
                        <div className="flex flex-wrap gap-2 pt-1">
                            {Array.isArray(STARTERS) && STARTERS.map((s, i) => (
                                <button
                                    key={i}
                                    onClick={() => send(s)}
                                    className="text-[11px] px-3 py-1.5 rounded-full transition-colors"
                                    style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.3)", color: "var(--gold)" }}
                                    data-testid={`support-starter-${i}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <Bubble key={i} m={m} />
                ))}
                {busy && <TypingBubble />}
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex gap-2 mt-3 px-4 md:px-0 pb-4 md:pb-0"
                data-testid="support-form"
            >
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t("supportChat.inputPlaceholder")}
                    className="flex-1 rounded-full px-5 py-3 text-sm text-white focus:outline-none"
                    style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.3)" }}
                    data-testid="support-input"
                />
                <button
                    type="submit"
                    disabled={busy || !text.trim()}
                    className="w-11 h-11 rounded-full grid place-items-center disabled:opacity-50"
                    style={{ background: "var(--gold)", color: "var(--navy)" }}
                    aria-label={t("common.submit")}
                    data-testid="support-send"
                >
                    <PaperPlaneRight size={13} weight="fill" />
                </button>
            </form>
        </div>
    );
}

function Bubble({ m }) {
    const { t } = useTranslation();
    const isUser = m.role === "user";
    const [showTrace, setShowTrace] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex gap-3 items-start ${isUser ? "flex-row-reverse" : ""}`}
        >
            <div
                className="w-9 h-9 rounded-full grid place-items-center shrink-0 text-base"
                style={{
                    background: isUser ? "rgba(255,255,255,0.08)" : "rgba(212,175,55,0.15)",
                    border: `1px solid ${isUser ? "rgba(255,255,255,0.14)" : "rgba(212,175,55,0.45)"}`,
                }}
            >
                {isUser ? <span className="text-[11px] text-white/85">{t("supportChat.you")}</span> : "👑"}
            </div>
            <div className="max-w-[85%]">
                <div
                    className="rounded-2xl px-4 py-3 text-sm text-white/90 whitespace-pre-wrap"
                    style={{
                        background: isUser ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${isUser ? "rgba(212,175,55,0.28)" : "rgba(255,255,255,0.07)"}`,
                    }}
                    data-testid={`support-bubble-${m.role}`}
                >
                    {m.text}
                </div>
                {!isUser && m.next_steps && m.next_steps.length > 0 && (
                    <ul className="mt-2 space-y-1">
                        {m.next_steps.map((s, j) => (
                            <li key={j} className="text-[12px] text-white/70 flex gap-2 items-start">
                                <span style={{ color: "var(--gold)" }}>›</span> {s}
                            </li>
                        ))}
                    </ul>
                )}
                {!isUser && m.delegations && m.delegations.length > 0 && (
                    <div className="mt-2">
                        <button
                            onClick={() => setShowTrace((v) => !v)}
                            className="text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-white flex items-center gap-1"
                            data-testid="support-trace-toggle"
                        >
                            <CaretDown size={10} className={showTrace ? "rotate-180" : ""} />
                            {t("supportChat.specialistsConsulted", { count: m.delegations.length })}
                            {m.escalate ? ` · ${t("supportChat.escalated")}` : ""}
                        </button>
                        <AnimatePresence>
                            {showTrace && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                                    className="mt-2 space-y-1.5 pl-2 border-l"
                                    style={{ borderColor: "rgba(212,175,55,0.3)" }}
                                >
                                    {m.delegations.map((d, k) => (
                                        <div key={k} className="flex items-center gap-2 text-[11px] text-white/70">
                                            <span>{AGENT_ICONS[d.agent_code] || "🤖"}</span>
                                            <span>{d.agent_code}</span>
                                            <span className="text-white/40">·</span>
                                            <span>{t("supportChat.confidence", { confidence: d.confidence })}</span>
                                            {d.escalation_required && <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(255,120,120,0.14)", color: "#FF8888" }}>{t("supportChat.flagged")}</span>}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function TypingBubble() {
    const { t } = useTranslation();
    return (
        <div className="flex gap-3 items-start" data-testid="support-typing">
            <div className="w-9 h-9 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.45)" }}>👑</div>
            <div className="rounded-2xl px-4 py-3 text-white/70 text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <span className="inline-flex gap-1 items-center">
                    <CircleNotch size={12} className="animate-spin" color="#D4AF37" />
                    <span className="text-white/50 text-[12px]">{t("supportChat.consulting")}</span>
                </span>
            </div>
        </div>
    );
}

/* Floating chat bubble launcher */
export function SupportBubble() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full grid place-items-center shadow-xl transition-transform hover:scale-105"
                style={{ background: "linear-gradient(135deg, var(--gold), #F4D06F)", color: "var(--navy)", boxShadow: "0 12px 40px rgba(212,175,55,0.4)" }}
                aria-label={t("supportChat.bubbleAria")}
                data-testid="support-bubble-open"
            >
                <Sparkle size={20} weight="fill" />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] flex items-end justify-end p-4 md:p-6"
                        style={{ background: "rgba(5,10,17,0.75)", backdropFilter: "blur(6px)" }}
                        onClick={() => setOpen(false)}
                        data-testid="support-bubble-modal"
                    >
                        <motion.div
                            onClick={(e) => e.stopPropagation()}
                            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
                            className="rounded-3xl w-full max-w-md relative"
                            style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
                        >
                            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">👑</span>
                                    <div>
                                        <div className="font-serif text-base text-white leading-none">{t("supportChat.conciergeTitle")}</div>
                                        <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "var(--gold)" }}>{t("supportChat.conciergeSubtitle")}</div>
                                    </div>
                                </div>
                                <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full grid place-items-center text-white/70 hover:text-white" aria-label={t("common.close")} data-testid="support-bubble-close">
                                    <X size={12} />
                                </button>
                            </div>
                            <SupportChat variant="bubble" onClose={() => setOpen(false)} />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
